import { Select, Modal, Upload } from "common-components";
import { UiUploadFilesModal } from "./UploadFilesModal.style";
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { useFirebaseStorage } from "utils/hooks/useFirebaseStorage";
import openNotificationWithIcon from "utils/hooks/useNotification";
import { CURRICULUM_OPTIONS_WITHOUT_ALL, DATA_TYPE } from "../constants";
import { Tabs } from "antd";
import {
	importExamChapterKnowledge,
	importExamChapterLevel,
	importSyllabusKnowledge,
	uploadChapters,
} from "api/chapter/index";
import useOptions from "./useOptions";

const UploadFilesModal = ({ isVisible, setIsVisible, refetch }) => {
	const { upload } = useFirebaseStorage();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [selectedCurriculum, setSelectedCurriculum] = useState(CURRICULUM_OPTIONS_WITHOUT_ALL[0].value);
	const [selectedKnowledgeFiles, setSelectedKnowledgeFiles] = useState([]);
	const [selectedChapterFiles, setSelectedChapterFiles] = useState([]);
	const [dataType, setDataType] = useState(String(DATA_TYPE.chapter));
	const [selectedEduSubject, setSelectedEduSubject] = useState("");
	const [selectedYear, setSelectedYear] = useState("");
	const [selectedVersion, setSelectedVersion] = useState("");
	const [selectedBook, setSelectedBook] = useState("");
	const { yearOptions, eduSubjectOptions, versionOptions, bookOptions } = useOptions(selectedYear, selectedEduSubject);
	const [isSubmitted, setIsSubmitted] = useState(false);

	const formErrors = useMemo(() => {
		const errors = {};
		switch (dataType) {
			case String(DATA_TYPE.chapter):
				if (!selectedCurriculum) errors.curriculum = "課綱欄位不得空白";
				if (!selectedKnowledgeFiles.length) errors.knowledgeFiles = "請上傳檔案";
				break;
			case String(DATA_TYPE.book):
				if (!selectedEduSubject) errors.eduSubject = "學制科目欄位不得空白";
				if (!selectedYear) errors.year = "適用年度欄位不得空白";
				if (!selectedCurriculum) errors.curriculum = "課綱欄位不得空白";
				if (!selectedVersion) errors.version = "版本欄位不得空白";
				if (!selectedBook) errors.book = "冊次欄位不得空白";
				if (!selectedKnowledgeFiles.length) errors.knowledgeFiles = "請上傳檔案";
				if (!selectedChapterFiles.length) errors.chapterFiles = "請上傳檔案";
				break;
			case String(DATA_TYPE.topic):
				if (!selectedEduSubject) errors.eduSubject = "學制科目欄位不得空白";
				if (!selectedYear) errors.year = "適用年度欄位不得空白";
				if (!selectedVersion) errors.version = "版本欄位不得空白";
				if (!selectedKnowledgeFiles.length) errors.knowledgeFiles = "請上傳檔案";
				break;
			default:
				break;
		}
		return errors;
	}, [
		dataType,
		selectedCurriculum,
		selectedKnowledgeFiles,
		selectedChapterFiles,
		selectedEduSubject,
		selectedYear,
		selectedVersion,
		selectedBook,
	]);

	const closeModal = () => {
		setIsVisible(false);
	};

	const handleSubmitChapter = async () => {
		const payload = await Promise.all(
			selectedKnowledgeFiles.map(async (file) => {
				const uid = uuidv4();
				const res = await upload(`chapter/${uid}/${file.name}`, file);
				return {
					curriculum: selectedCurriculum,
					dataType,
					url: res.url,
					fileName: file.name,
				};
			}),
		);
		const result = await uploadChapters(payload);
		if (result.error === 200) {
			openNotificationWithIcon("success", "檔案已上傳！");
			closeModal();
			refetch();
		} else {
			openNotificationWithIcon("error", "上傳失敗！請檢查檔名格式是否正確");
		}
	};

	const handleSubmitBook = async () => {
		const [knowledgePayload] = await Promise.all(
			selectedKnowledgeFiles.map(async (file) => {
				const uid = uuidv4();
				const res = await upload(`chapter/${uid}/${file.name}`, file);
				return {
					url: res.url,
					fileName: file.name,
				};
			}),
		);
		await importExamChapterKnowledge(selectedEduSubject, knowledgePayload);
		const [payload] = await Promise.all(
			selectedChapterFiles.map(async (file) => {
				const uid = uuidv4();
				const res = await upload(`chapter/${uid}/${file.name}`, file);
				return {
					curriculum: selectedCurriculum,
					url: res.url,
					fileName: file.name,
				};
			}),
		);
		const bookID = `${selectedYear}${selectedVersion}-${selectedEduSubject}${selectedBook}`;
		const result = await importExamChapterLevel(bookID, payload);
		if (result?.isSuccess) {
			openNotificationWithIcon("success", "上傳成功！");
			closeModal();
			refetch();
		} else {
			openNotificationWithIcon("error", `上傳失敗！${result.message}`);
		}
	};

	const handleSubmitTopic = async () => {
		const [knowledgePayload] = await Promise.all(
			selectedKnowledgeFiles.map(async (file) => {
				const uid = uuidv4();
				const res = await upload(`chapter/${uid}/${file.name}`, file);
				return {
					eduSubject: selectedEduSubject,
					url: res.url,
					fileName: file.name,
				};
			}),
		);
		const result = await importSyllabusKnowledge(knowledgePayload);
		if (result?.isSuccess) {
			openNotificationWithIcon("success", "上傳成功！");
			closeModal();
			refetch();
		} else {
			openNotificationWithIcon("error", `上傳失敗！${result.message}`);
		}
	};

	const handleSubmit = async () => {
		setIsSubmitted(true);
		if (Object.keys(formErrors).length) return;

		setIsSubmitting(true);
		try {
			switch (dataType) {
				case String(DATA_TYPE.chapter): {
					await handleSubmitChapter();
					return;
				}
				case String(DATA_TYPE.book): {
					await handleSubmitBook();
					return;
				}
				case String(DATA_TYPE.topic): {
					await handleSubmitTopic();
					return;
				}
				default:
					return;
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	useEffect(() => {
		setIsSubmitted(false);
	}, [
		dataType,
		selectedCurriculum,
		selectedKnowledgeFiles,
		selectedChapterFiles,
		selectedEduSubject,
		selectedYear,
		selectedVersion,
		selectedBook,
	]);

	useEffect(() => {
		setSelectedKnowledgeFiles([]);
		setSelectedChapterFiles([]);
	}, [dataType]);

	return (
		<UiUploadFilesModal>
			<Modal
				confirmLoading={isSubmitting}
				visible={isVisible}
				onCancel={closeModal}
				okText={isSubmitting ? "上傳中..." : "確定"}
				onOk={handleSubmit}
				title="上傳檔案"
				width="560px">
				<Tabs activeKey={dataType} onChange={setDataType}>
					<Tabs.TabPane tab="章節重點" key={String(DATA_TYPE.chapter)}>
						<div className="modalContent">
							<Select
								label="課綱"
								options={CURRICULUM_OPTIONS_WITHOUT_ALL}
								value={selectedCurriculum}
								onChange={(value) => setSelectedCurriculum(value)}
								message={(isSubmitted && formErrors.curriculum) || ""}
							/>
							<div>
								<div>匯入知識向度</div>
								<Upload
									fileList={selectedKnowledgeFiles}
									onChange={setSelectedKnowledgeFiles}
									prompt="(副檔名 : .xls/.xlsx)"
									acceptFileType=".xls,.xlsx"
									multiple
									message={(isSubmitted && formErrors.knowledgeFiles) || ""}
								/>
							</div>
							<ul className="uploadFileRules">
								<li>
									檔案命名方式：<b>學年度學期_版本學制科目</b>
								</li>
								<li>
									範例：
									<ul>
										<li>113上_南一國中國文</li>
										<li>113下_南一高中選修數學(甲)乙</li>
										<li>113下_康軒國中理化</li>
									</ul>
								</li>
							</ul>
						</div>
					</Tabs.TabPane>
					<Tabs.TabPane tab="大考冊次" key={String(DATA_TYPE.book)}>
						<div className="modalContent">
							<Select
								label="學制科目"
								options={eduSubjectOptions}
								value={selectedEduSubject}
								onChange={(value) => setSelectedEduSubject(value)}
								message={(isSubmitted && formErrors.eduSubject) || ""}
							/>
							<Select
								label="適用年度"
								options={yearOptions}
								value={selectedYear}
								onChange={(value) => setSelectedYear(value)}
								message={(isSubmitted && formErrors.year) || ""}
							/>
							<Select
								label="課綱"
								options={CURRICULUM_OPTIONS_WITHOUT_ALL}
								value={selectedCurriculum}
								onChange={(value) => setSelectedCurriculum(value)}
								message={(isSubmitted && formErrors.curriculum) || ""}
							/>
							<Select
								label="版本"
								options={versionOptions}
								value={selectedVersion}
								onChange={(value) => setSelectedVersion(value)}
								message={(isSubmitted && formErrors.version) || ""}
							/>
							<Select
								label="冊次"
								options={bookOptions}
								value={selectedBook}
								onChange={(value) => setSelectedBook(value)}
								message={(isSubmitted && formErrors.book) || ""}
							/>
							<div>
								<div>匯入知識向度</div>
								<Upload
									fileList={selectedKnowledgeFiles}
									onChange={setSelectedKnowledgeFiles}
									prompt={
										<>
											僅限上傳一個 Microsoft Excel 檔<br />
											(副檔名 : .xls/.xlsx)
										</>
									}
									acceptFileType=".xls,.xlsx"
									message={(isSubmitted && formErrors.knowledgeFiles) || ""}
								/>
							</div>
							<div>
								<div>匯入大考章節</div>
								<Upload
									fileList={selectedChapterFiles}
									onChange={setSelectedChapterFiles}
									prompt={
										<>
											僅限上傳一個 Microsoft Excel 檔<br />
											(副檔名 : .xls/.xlsx)
										</>
									}
									acceptFileType=".xls,.xlsx"
									message={(isSubmitted && formErrors.chapterFiles) || ""}
								/>
							</div>
						</div>
					</Tabs.TabPane>
					<Tabs.TabPane tab="主題式無課綱" key={String(DATA_TYPE.topic)}>
						<div className="modalContent">
							<Select
								label="學制科目"
								options={eduSubjectOptions}
								value={selectedEduSubject}
								onChange={(value) => setSelectedEduSubject(value)}
								message={(isSubmitted && formErrors.eduSubject) || ""}
							/>
							<Select
								label="適用年度"
								options={yearOptions}
								value={selectedYear}
								onChange={(value) => setSelectedYear(value)}
								message={(isSubmitted && formErrors.year) || ""}
							/>
							<Select
								label="版本"
								options={versionOptions}
								value={selectedVersion}
								onChange={(value) => setSelectedVersion(value)}
								message={(isSubmitted && formErrors.version) || ""}
							/>
							<div>
								<div>匯入知識向度</div>
								<Upload
									fileList={selectedKnowledgeFiles}
									onChange={setSelectedKnowledgeFiles}
									prompt={
										<>
											僅限上傳一個 Microsoft Excel 檔<br />
											(副檔名 : .xls/.xlsx)
										</>
									}
									acceptFileType=".xls,.xlsx"
									message={(isSubmitted && formErrors.knowledgeFiles) || ""}
								/>
							</div>
						</div>
					</Tabs.TabPane>
				</Tabs>
			</Modal>
		</UiUploadFilesModal>
	);
};
UploadFilesModal.propTypes = {
	isVisible: PropTypes.bool.isRequired,
	setIsVisible: PropTypes.func.isRequired,
	refetch: PropTypes.func.isRequired,
};

export default UploadFilesModal;
